/**
 * Data transformation utilities for benefit classes
 *
 * Handles conversion between API format and grid format
 */

import {
  BenefitClassResponse,
  CoverageResponse,
} from "../../api/benefitClasses";

import { BenefitsClassRow } from "./BenefitsClassGrid";

/**
 * Coverage type to grid field name mapping
 * Maps backend coverage types to frontend grid column field names
 */
const COVERAGE_TYPE_MAP: Record<
  string,
  keyof Omit<BenefitsClassRow, "id" | "className">
> = {
  dental: "dental",
  vision: "vision",
  std: "std",
  ltd: "ltd",
  voluntaryLifeAdnd: "voluntaryLife",
  eap: "eap",
  hospitalIndemnity: "hi",
  basicLifeAdnd: "basicLife",
  criticalIllness: "ci",
  accident: "accident",
  cancer: "cancer",
  individualDisabilityInsurance: "idi",
  fmla: "fmla",
  wholeLife: "wholeLife",
};

/**
 * Transforms API benefit class data to grid row format
 *
 * @param benefitClasses - Array of benefit classes from API
 * @returns Array of benefit class rows for AgGrid
 *
 * @example
 * ```ts
 * const gridData = transformToGridData([
 *   {
 *     id: "class-1",
 *     name: "Class A",
 *     coverages: [
 *       { id: "cov-1", type: "dental", ... },
 *       { id: "cov-2", type: "vision", ... }
 *     ]
 *   }
 * ]);
 * // gridData[0].dental === true
 * // gridData[0].vision === true
 * // gridData[0].std === false
 * ```
 */
export const transformToGridData = (
  benefitClasses: BenefitClassResponse[],
): BenefitsClassRow[] =>
  benefitClasses.map((bc) => {
    // Initialize row with all coverages set to false
    const row: BenefitsClassRow = {
      id: bc.id,
      className: bc.name,
      dental: false,
      vision: false,
      std: false,
      ltd: false,
      voluntaryLife: false,
      eap: false,
      hi: false,
      basicLife: false,
      ci: false,
      accident: false,
      cancer: false,
      idi: false,
      fmla: false,
      wholeLife: false,
    };

    // Set true for coverages that are assigned to this class
    bc.coverages?.forEach((coverage) => {
      const fieldName = COVERAGE_TYPE_MAP[coverage.type];
      if (fieldName) {
        row[fieldName] = true;
      }
    });

    return row;
  });

/**
 * Transforms grid row data to API batch update format
 *
 * @param rows - Array of benefit class rows from grid
 * @param allCoverages - All available coverages (to map types to IDs)
 * @returns Array of updates for batch API call
 *
 * @example
 * ```ts
 * const updates = transformToApiFormat(
 *   [
 *     { id: "class-1", className: "Class A", dental: true, vision: true, ... }
 *   ],
 *   [
 *     { id: "cov-1", type: "dental", ... },
 *     { id: "cov-2", type: "vision", ... }
 *   ]
 * );
 * // updates[0].coverageIds === ["cov-1", "cov-2"]
 * ```
 */
export const transformToApiFormat = (
  rows: BenefitsClassRow[],
  allCoverages: CoverageResponse[],
): {
  id: string;
  name: string;
  coverageIds: string[];
}[] => {
  // Create reverse map: coverage type -> coverage ID
  const coverageIdMap = new Map<string, string>();
  allCoverages.forEach((coverage) => {
    coverageIdMap.set(coverage.type, coverage.id);
  });

  return rows.map((row) => {
    // Collect coverage IDs for all selected (true) coverages
    const coverageIds: string[] = [];

    Object.entries(COVERAGE_TYPE_MAP).forEach(([coverageType, fieldName]) => {
      if (row[fieldName] === true) {
        const coverageId = coverageIdMap.get(coverageType);
        if (coverageId) {
          coverageIds.push(coverageId);
        }
      }
    });

    return {
      id: row.id,
      name: row.className,
      coverageIds,
    };
  });
};

/**
 * Extracts unique coverages from benefit classes
 *
 * @param benefitClasses - Array of benefit classes from API
 * @returns Array of unique coverages
 *
 * @example
 * ```ts
 * const coverages = extractCoverages(benefitClasses);
 * // Used to build the grid columns and for transformation
 * ```
 */
export const extractCoverages = (
  benefitClasses: BenefitClassResponse[],
): CoverageResponse[] => {
  const coverageMap = new Map<string, CoverageResponse>();

  benefitClasses.forEach((bc) => {
    bc.coverages?.forEach((coverage) => {
      if (!coverageMap.has(coverage.id)) {
        coverageMap.set(coverage.id, coverage);
      }
    });
  });

  return Array.from(coverageMap.values());
};

// UTF-8 bytes, not characters: the BTX backend (MaxBenefitClassNameLen) caps the name by byte
// length and truncates anything longer on create.
const MAX_CLASS_NAME_BYTES = 560;
const MIN_CLASS_NAME_LENGTH = 2;

const utf8ByteLength = (value: string): number =>
  new TextEncoder().encode(value).length;

export const isCreatableClassName = (className: string): boolean =>
  className.length >= MIN_CLASS_NAME_LENGTH &&
  utf8ByteLength(className) <= MAX_CLASS_NAME_BYTES;
